import Glider from 'glider-js';
import debounce from 'lodash.debounce';

import { CSS_PREFIX } from './constants';

const BASE_CAROUSEL_CLASS = `${CSS_PREFIX}related-players-carousel`;
const BASE_CAROUSEL_ITEM_CLASS = `${BASE_CAROUSEL_CLASS}-item`;
const CAROUSEL_TAB_ITEM_ACTIVE = `${BASE_CAROUSEL_CLASS}__tabs-item--active`;
const TAB_SELECTOR = `button.${BASE_CAROUSEL_CLASS}__tabs-item`;
const ACTIVE_TAB_SELECTOR = `.${BASE_CAROUSEL_CLASS}__tabs-item--active`;

// eslint-disable-next-line jest/require-hook
let tabItems: Element[] = [];

type RelatedPlayersCarouselGlobal = {
  suppressNextTabAnimation?: boolean;
};

const getCarouselGlobal = (): RelatedPlayersCarouselGlobal => globalThis as unknown as RelatedPlayersCarouselGlobal;
const getActiveTab = (): HTMLButtonElement | null => document.querySelector<HTMLButtonElement>(ACTIVE_TAB_SELECTOR);
const getTabByCategory = (category: string): HTMLButtonElement | null =>
  document.querySelector<HTMLButtonElement>(`${TAB_SELECTOR}[data-category="${category}"]`);

const setActiveTab = (tab: HTMLButtonElement | null): void => {
  if (!tab) {
    return;
  }

  getActiveTab()?.classList.remove(CAROUSEL_TAB_ITEM_ACTIVE);
  tab.classList.add(CAROUSEL_TAB_ITEM_ACTIVE);
};

const clearTabAnimationClasses = (): void => {
  document
    .querySelectorAll<HTMLButtonElement>(
      `.${BASE_CAROUSEL_CLASS}__tabs-item.${CSS_PREFIX}opening--l, ` +
        `.${BASE_CAROUSEL_CLASS}__tabs-item.${CSS_PREFIX}opening--r, ` +
        `.${BASE_CAROUSEL_CLASS}__tabs-item.${CSS_PREFIX}closing--l, ` +
        `.${BASE_CAROUSEL_CLASS}__tabs-item.${CSS_PREFIX}closing--r`
    )
    .forEach((tab) =>
      tab.classList.remove(
        `${CSS_PREFIX}opening--l`,
        `${CSS_PREFIX}opening--r`,
        `${CSS_PREFIX}closing--l`,
        `${CSS_PREFIX}closing--r`
      )
    );
};

const scrollToFirstInCat = (cat: string, glider: Glider<HTMLElement>): void => {
  const slideIndex = document.querySelector<HTMLAnchorElement>(`.${BASE_CAROUSEL_ITEM_CLASS}[data-category="${cat}"]`)
    ?.dataset.gslide;

  if (slideIndex) {
    glider.scrollItem(parseInt(slideIndex, 10), false);
  }
};

const getActiveCategory = (carouselGlider: Glider<HTMLElement>): string | undefined => {
  const activeSlide = document.querySelector<HTMLElement>(`.${BASE_CAROUSEL_ITEM_CLASS}.active`);
  const activeCategory = activeSlide?.dataset.category;
  const lastVisibleSlide = document.querySelector<HTMLElement>(`.${BASE_CAROUSEL_ITEM_CLASS}.visible:last-of-type`);
  const lastVisibleCategory = lastVisibleSlide?.dataset.category;
  const current = typeof carouselGlider.slide === 'number' ? carouselGlider.slide : NaN;
  const total = typeof carouselGlider.slides?.length === 'number' ? carouselGlider.slides.length : NaN;
  const isAtEnd = Number.isFinite(current) && Number.isFinite(total) ? current >= total - 1 : false;

  if (isAtEnd && lastVisibleCategory) {
    return lastVisibleCategory;
  }

  if (lastVisibleCategory && activeCategory && lastVisibleCategory !== activeCategory) {
    return lastVisibleCategory;
  }

  return activeCategory ?? lastVisibleCategory;
};

const animateTabSwitch = (tabToActivate: HTMLButtonElement | null, activeTab: HTMLButtonElement | null): void => {
  if (!tabToActivate || !activeTab) {
    return;
  }

  const tabToActivateIndex = tabItems.indexOf(tabToActivate);
  const activeTabIndex = tabItems.indexOf(activeTab);

  if (tabToActivateIndex !== activeTabIndex) {
    tabToActivate.classList.remove(`${CSS_PREFIX}closing--l`, `${CSS_PREFIX}closing--r`);
    activeTab.classList.remove(`${CSS_PREFIX}opening--l`, `${CSS_PREFIX}opening--r`);
  }

  if (tabToActivateIndex > activeTabIndex) {
    tabToActivate.classList.add(`${CSS_PREFIX}opening--l`);
    activeTab.classList.add(`${CSS_PREFIX}closing--r`);
  } else if (tabToActivateIndex < activeTabIndex) {
    tabToActivate.classList.add(`${CSS_PREFIX}opening--r`);
    activeTab.classList.add(`${CSS_PREFIX}closing--l`);
  }
};

const debouncedSliderHandler = debounce(
  (tabsGlider: Glider<HTMLElement> | null, carouselGlider: Glider<HTMLElement>) => {
    if (getCarouselGlobal().suppressNextTabAnimation) {
      getCarouselGlobal().suppressNextTabAnimation = false;
      return;
    }

    const category = getActiveCategory(carouselGlider);
    if (!category) {
      return;
    }

    const tabToActivate = getTabByCategory(category);
    const activeTab = getActiveTab();

    setActiveTab(tabToActivate);

    if (tabToActivate) {
      const slideIndex = tabToActivate.dataset.gslide;
      if (slideIndex) {
        tabsGlider?.scrollItem(parseInt(slideIndex, 10), false);
      }
    }

    animateTabSwitch(tabToActivate, activeTab);
  },
  150
);

export default () => {
  const carouselWrapper = document.querySelector<HTMLDivElement>(`.${BASE_CAROUSEL_CLASS}__carousel`);
  const tabsCarouselWrapper = document.querySelector<HTMLDivElement>(`.${BASE_CAROUSEL_CLASS}__tabs`);
  const tabItemsQuery = tabsCarouselWrapper?.querySelectorAll(`.${BASE_CAROUSEL_CLASS}__tabs-track button`);

  if (tabItemsQuery) {
    tabItems = Array.from(tabItemsQuery);
  }

  if (!carouselWrapper) {
    return;
  }

  const glider = new Glider(carouselWrapper, {
    draggable: true,
    skipTrack: true,
    scrollLock: true,
    exactWidth: true,
    itemWidth: 235,
    slidesToShow: 'auto',
    slidesToScroll: 1,
    duration: 0.5,
    rewind: true,
    arrows: {
      prev: `.${BASE_CAROUSEL_CLASS}__arrow-left`,
      next: `.${BASE_CAROUSEL_CLASS}__arrow-right`,
    },
    responsive: [
      {
        breakpoint: 740,
        settings: {
          itemWidth: 255,
          slidesToShow: 'auto',
          slidesToScroll: 1,
        },
      },
    ],
  });

  const tabsGlider = tabsCarouselWrapper
    ? new Glider(tabsCarouselWrapper, {
        slidesToShow: 'auto',
        slidesToScroll: 1,
        draggable: true,
        skipTrack: true,
        scrollLock: false,
        rewind: false,
        exactWidth: true,
        itemWidth: 140,
        arrows: { prev: null, next: null },
      })
    : null;

  carouselWrapper.addEventListener('glider-slide-visible', () => debouncedSliderHandler(tabsGlider, glider));

  document.querySelectorAll<HTMLButtonElement>(TAB_SELECTOR)?.forEach((button) =>
    button.addEventListener('click', (e) => {
      e.preventDefault();

      const category = (e.target as HTMLElement | null)?.dataset?.category;
      if (!category) {
        return;
      }

      setActiveTab(getTabByCategory(category));
      getCarouselGlobal().suppressNextTabAnimation = true;
      clearTabAnimationClasses();
      scrollToFirstInCat(category, glider);
    })
  );

  const currentCat = document.querySelector<HTMLDivElement>(`aside.${BASE_CAROUSEL_CLASS}`)?.dataset.currentCat;
  if (currentCat) {
    scrollToFirstInCat(currentCat, glider);
  }
};
